import {
  Sparkles,
  Lightbulb,
  Activity,
  Bot,
  Building2,
  ClipboardList,
  Coins,
  Gauge,
  Contact as ContactIcon,
  GraduationCap,
  HardHat,
  History,
  KanbanSquare,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  MapPinned,
  Megaphone,
  MessagesSquare,
  Radio,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  UserCog,
  TrendingUp,
  UserMinus,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { hasRole, type Role } from '@conversia/shared-types';
import { enlaceDeGarantias } from './vista-por-rol';

/**
 * A4 · EL MENÚ, agrupado por lo que la persona INTENTA HACER.
 *
 * Antes era una lista plana de 19 entradas ordenada por frecuencia de uso. Con
 * ese largo, encontrar algo es leer los 19 nombres. Ahora se agrupa por la
 * intención: "quiero ver cómo va el día", "quiero vender", "quiero coordinar
 * una instalación".
 *
 * Los grupos van del uso diario a lo ocasional: `hoy` primero, `sistema`
 * último. Es el mismo criterio que tenía la lista plana, aplicado a los grupos.
 *
 * LOS TRES PARES DE NOMBRES QUE CONFUNDÍAN se resolvieron acá, y no eran
 * pantallas duplicadas: eran nombres malos para pantallas distintas.
 *
 *   /clientes vs /contacts   — una es quién COMPRÓ, la otra quién ESCRIBIÓ.
 *                              Ahora los nombres lo dicen.
 *   /installation vs /settings — una es el NEGOCIO (catálogos, mensajes), la
 *                              otra lo TÉCNICO (claves, avisos).
 *   /consumos vs /limites    — una es lo GASTADO, la otra el TOPE.
 *
 * Solo cambian las etiquetas. Ninguna URL se toca.
 */

/**
 * Permisos que el ROL no alcanza a decidir y que contesta la API.
 *
 * `historialCopiloto`: lo ve el OWNER o quien tenga cargado el teléfono de un
 * dueño activo del directorio del copiloto. Eso no se puede saber desde el rol.
 */
export type PermisoDelMenu = 'historialCopiloto';
export type PermisosDelMenu = Partial<Record<PermisoDelMenu, boolean>>;

/**
 * Los módulos que un negocio puede tener encendidos o apagados.
 *
 * El producto es uno y los clientes son distintos: Osito vende ropa y no
 * instala nada, así que no ve Trabajos, Garantías ni Técnicos en la calle.
 * Sin esto, el equipo de la tienda abre su panel y encuentra seis entradas de
 * otro rubro — y aprende que media aplicación no es para ellos.
 */
export type ModuloDelMenu = 'instalaciones' | 'campanas' | 'descartes' | 'entrenamiento';
export type ModulosDelMenu = Partial<Record<ModuloDelMenu, boolean>>;

export interface ItemMenu {
  to: string;
  label: string;
  icon: LucideIcon;
  admin?: boolean;
  /** Se muestra solo si este módulo está encendido para el negocio. */
  modulo?: ModuloDelMenu;
  /** Reservado del OWNER: la API responde 403 a un ADMIN; acá solo se oculta. */
  owner?: boolean;
  /**
   * Se muestra solo con este permiso en `true`. El OWNER lo tiene siempre: la
   * API siempre lo deja, y así no espera una consulta para ver su menú.
   */
  permiso?: PermisoDelMenu;
}

export interface GrupoMenu {
  id: string;
  label: string;
  items: ItemMenu[];
  /** Si el grupo entero pertenece a un módulo. */
  modulo?: ModuloDelMenu;
}

/** El enlace del atajo «Garantías», o `null` si Trabajos aún no lo filtra. */
const ATAJO_GARANTIAS = enlaceDeGarantias();

export const GRUPOS: GrupoMenu[] = [
  {
    id: 'hoy',
    label: 'Hoy',
    items: [
      { to: '/', label: 'Resumen del día', icon: LayoutDashboard },
      { to: '/conversations', label: 'Conversaciones', icon: MessagesSquare },
      // Estaba desplegado en producción desde el 10-sep y no tenía entrada:
      // solo se llegaba escribiendo la URL a mano.
      { to: '/pendientes', label: 'Pendientes', icon: ListTodo, admin: true },
    ],
  },
  {
    id: 'vender',
    label: 'Vender',
    items: [
      { to: '/crm', label: 'Embudo de ventas', icon: KanbanSquare },
      // "Quién compró" era el nombre en el negocio de instalaciones, donde una
      // compra es un trabajo completado. Acá la cartera se llena con quien
      // escribió y qué buscaba: el nombre viejo prometía una columna que esta
      // pantalla no puede llenar.
      { to: '/clientes', label: 'Clientes', icon: Users, admin: true },
      { to: '/contacts', label: 'Quién escribió', icon: ContactIcon },
      // Apagada para Osito: el módulo no tiene motor de envío.
      { to: '/campaigns', label: 'Campañas', icon: Megaphone, modulo: 'campanas' },
      // Propia de Osito: 85 de cada 100 chats terminan acá, y un descarte que
      // nadie mira es indistinguible de un lead perdido por un filtro mal
      // calibrado.
      { to: '/descartes', label: 'Descartados', icon: UserMinus, admin: true, modulo: 'descartes' },
      // Portada de Tony Boga el 4-nov-2026. Va en «Vender» y no en
      // «Instalaciones» porque habla de anuncios y de cierre, no de técnicos.
      // Solo admin: son números de plata y de desempeño de la gente.
      { to: '/eficiencia', label: 'Eficiencia', icon: TrendingUp, admin: true },
      { to: '/publicidad', label: 'Publicidad', icon: Megaphone, admin: true },
    ],
  },
  {
    id: 'instalaciones',
    label: 'Instalaciones',
    // Grupo COMPLETO del rubro de instalaciones. Apagado para Osito.
    modulo: 'instalaciones',
    items: [
      { to: '/jobs', label: 'Trabajos', icon: Wrench, admin: true },
      // El atajo del que está encima de las garantías: Trabajos ya filtrado.
      // Sale de `vista-por-rol`, y es `null` —y entonces no hay entrada—
      // mientras Trabajos no acepte ese filtro: una entrada que dice
      // «Garantías» y abre la lista completa miente.
      ...(ATAJO_GARANTIAS
        ? [{ to: ATAJO_GARANTIAS, label: 'Garantías', icon: ShieldAlert, admin: true }]
        : []),
      { to: '/desempeno', label: 'Desempeño de técnicos', icon: Gauge, admin: true },
      { to: '/despachos', label: 'Despachos', icon: ClipboardList, admin: true },
      { to: '/tracking', label: 'Técnicos en la calle', icon: MapPinned, admin: true },
      { to: '/employees', label: 'Empleados', icon: HardHat, admin: true },
    ],
  },
  {
    id: 'asistente',
    label: 'El asistente',
    items: [
      // Va PRIMERA del grupo, y arriba de "Agentes AI", a proposito: es la que
      // se usa a diario. Las otras dos se tocan una vez y se olvidan.
      { to: '/asistente', label: 'Hablar con el asistente', icon: Sparkles },
      // Sin `admin`: quien encuentra los problemas es quien usa el sistema
      // todos los dias. Ponerle un rol delante es la forma mas rapida de no
      // enterarse de nada.
      { to: '/pedidos', label: 'Qué quieres que cambiemos', icon: Lightbulb },
      // Lo que todo el equipo habló con el asistente. No es de admin: lo decide
      // el permiso que contesta la API (dueño del directorio u OWNER).
      {
        to: '/historial-copiloto',
        label: 'Historial del copiloto',
        icon: History,
        permiso: 'historialCopiloto',
      },
      { to: '/agents', label: 'Agentes AI', icon: Bot },
      { to: '/entrenamiento', label: 'Entrenamiento', icon: GraduationCap, admin: true, modulo: 'entrenamiento' },
      { to: '/channels', label: 'Canales', icon: Radio },
    ],
  },
  {
    id: 'ajustes',
    label: 'Ajustes',
    items: [
      { to: '/installation', label: 'Mi negocio', icon: Building2, admin: true },
      // La tarjeta del resumen se puede ocultar; esta entrada queda para volver.
      { to: '/tareas', label: 'Lo que falta configurar', icon: ListChecks, admin: true },
      { to: '/settings', label: 'Ajustes técnicos', icon: SettingsIcon, admin: true },
      { to: '/users', label: 'Usuarios', icon: UserCog, admin: true },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    items: [
      // ── EL GRUPO SISTEMA ES SOLO DEL DUEÑO Y DE KERVENS ──────────────────
      //
      // Kervens (2-oct-2026), con el menú abierto en la mano: «en los perfiles
      // que no son admin o kervens, esto no debe verse ni muerto».
      //
      // Es la misma regla que ya rige para los avisos por WhatsApp —«tienes
      // prohibido dar Estado del sistema, Gasto de iA, y Tope de gasto, estas
      // informaciones son nada mas para Kervens»— aplicada a la pantalla.
      //
      // «Estado del sistema» estaba en `admin`, no en `owner`. Hoy no se nota
      // porque los tres vendedores son AGENT, pero Kervens ha pedido subirlos a
      // administrador para que puedan hacer lo mismo que Eyevis. El día que eso
      // pase, un desplegable en la pantalla de Usuarios les habría abierto el
      // estado del sistema sin que nadie lo notara.
      { to: '/sistema', label: 'Estado del sistema', icon: Activity, owner: true },
      { to: '/consumos', label: 'Gasto de IA', icon: Coins, owner: true },
      { to: '/limites', label: 'Topes de gasto', icon: ShieldCheck, owner: true },
    ],
  },
];

/**
 * La RUTA de una entrada, sin el `?query`. PURO.
 *
 * Una entrada puede abrir una pantalla con filtros puestos (el atajo
 * «Garantías» va a `/jobs?garantia=1`). El mapa del sistema y el grupo de la
 * pantalla actual razonan en rutas, no en enlaces: si el `?query` viajara con
 * la ruta, el cerrojo del mapa buscaría una pantalla `/jobs?garantia=1` que no
 * existe.
 */
export function rutaDeItem(to: string): string {
  return to.split('?')[0] ?? to;
}

/** Todas las rutas del menú. Lo usa el test de regresión y el mapa de A8. */
export const TODAS_LAS_RUTAS_DEL_MENU: string[] = GRUPOS.flatMap((g) =>
  g.items.map((i) => rutaDeItem(i.to)),
);

/**
 * Los grupos que este rol puede ver, sin los grupos que le quedan vacíos.
 *
 * Lo segundo importa: un encabezado "Sistema" que al desplegarse no tiene nada
 * adentro es peor que no mostrarlo.
 *
 * `permisos` son los que contesta la API. Sin pasarlos, lo que depende de un
 * permiso queda oculto (salvo para el OWNER): mejor no mostrar un instante que
 * mostrar lo que no corresponde.
 */
export function gruposVisibles(
  rol: Role | undefined,
  permisos: PermisosDelMenu = {},
  modulos: ModulosDelMenu = {},
): GrupoMenu[] {
  /**
   * Un módulo sin respuesta todavía se trata como ENCENDIDO. Mientras la config
   * carga es mejor mostrar de más un instante que hacer parpadear el menú
   * entero; y si la consulta falla, el panel queda completo en vez de mutilado.
   */
  const activo = (m: ModuloDelMenu | undefined): boolean => m === undefined || modulos[m] !== false;

  return GRUPOS.filter((g) => activo(g.modulo)).map((g) => ({
    ...g,
    items: g.items.filter((i) => {
      if (!activo(i.modulo)) return false;
      if (i.owner && rol !== 'OWNER') return false;
      if (i.permiso && rol !== 'OWNER' && (!rol || permisos[i.permiso] !== true)) return false;
      // Sin rol resuelto todavia (primer render) se oculta lo protegido: mejor
      // un menu corto un instante que mostrar lo que no corresponde.
      if (i.admin && (!rol || !hasRole(rol, 'ADMIN'))) return false;
      return true;
    }),
  })).filter((g) => g.items.length > 0);
}

/**
 * A qué grupo pertenece una ruta, para abrirlo solo al entrar. `null` si no
 * está en el menú.
 *
 * Resuelve subrutas (`/conversations/abc123` → el grupo de `/conversations`)
 * eligiendo siempre la coincidencia MÁS LARGA: con `/` en el menú, una
 * comparación por prefijo a secas haría que todo cayera en el primer grupo.
 */
export function grupoDeRuta(ruta: string): string | null {
  let mejor: { id: string; largo: number } | null = null;

  for (const g of GRUPOS) {
    for (const item of g.items) {
      const suRuta = rutaDeItem(item.to);
      const coincide =
        suRuta === '/' ? ruta === '/' : ruta === suRuta || ruta.startsWith(`${suRuta}/`);
      if (coincide && (!mejor || suRuta.length > mejor.largo)) {
        mejor = { id: g.id, largo: suRuta.length };
      }
    }
  }
  return mejor?.id ?? null;
}
